import { Moon, Sunrise, Croissant, TrainFront } from "lucide-react";
import { SPOTS } from "../../data/planner";
import { DINNER_VENUES, nearestVenues } from "../../data/meals";
import { fmtDate, fmtTime } from "../../data/plannerDates";

const NYHAVN = [55.6797, 12.5912];
const kmBetween = (a, b) => {
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLng = ((b[1] - a[1]) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos((a[0] * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(x));
};

const Shell = ({ testid, Icon, kicker, date, children }) => (
  <article data-testid={testid} className="rounded-2xl border border-dashed border-zinc-300 bg-[#FBFAF7] p-4 md:p-5">
    <div className="flex items-center gap-2 mb-2">
      <span className="grid place-items-center w-7 h-7 rounded-full bg-black text-white">
        <Icon size={14} />
      </span>
      <div>
        <p className="font-display font-900 text-sm leading-none">{kicker}</p>
        <p className="font-body font-700 text-[11px] text-black/50 mt-0.5">{fmtDate(date)}</p>
      </div>
    </div>
    <div className="font-body text-sm leading-relaxed text-black/75 space-y-1.5">{children}</div>
  </article>
);

// The day you land late: check in, then an easy first evening.
export const ArrivalEveningCard = ({ date, from, hotel, month }) => {
  const near = hotel?.coords || NYHAVN;
  const venues = nearestVenues(DINNER_VENUES, near, { month, maxKm: hotel ? 2 : 1.5 });
  return (
    <Shell testid="planner-arrival-evening" Icon={Moon} kicker="Arrival evening" date={date}>
      <p>
        You&apos;re free from <span className="font-800 text-black">{fmtTime(from)}</span> — check in, drop the bags and keep it easy. The city is at its best on foot after dark: Nyhavn&apos;s
        lights, the canals around Christianshavn or a lap of the lakes.
      </p>
      {venues.length > 0 && (
        <p>
          <span className="font-800 text-black">Dinner {hotel ? "near your hotel" : "in the centre"}:</span>{" "}
          {venues.map((v, i) => (
            <span key={v.name}>
              {i > 0 && " · "}
              <span className="font-700 text-black/85">{v.name}</span> ({v.tag})
            </span>
          ))}
        </p>
      )}
    </Shell>
  );
};

// The day you leave before noon: a bakery breakfast, then off.
export const DepartureMorningCard = ({ date, by, hotel }) => {
  const near = hotel?.coords || NYHAVN;
  const bakeries = SPOTS.filter((s) => s.crawl)
    .map((s) => ({ s, km: kmBetween(near, s.coords) }))
    .sort((a, b) => a.km - b.km)
    .slice(0, 2);
  return (
    <Shell testid="planner-departure-morning" Icon={Sunrise} kicker="Departure morning" date={date}>
      <p>
        Out by <span className="font-800 text-black">{fmtTime(by)}</span> — no sightseeing, just a proper Danish breakfast.
        {bakeries.length > 0 && (
          <>
            {" "}
            <Croissant size={13} className="inline -mt-0.5 text-[#DF3226]" />{" "}
            {bakeries.map((b, i) => (
              <span key={b.s.id}>
                {i > 0 && " or "}
                <span className="font-700 text-black/85">{b.s.name}</span>
              </span>
            ))}{" "}
            {hotel ? "are the closest good bakeries to your hotel." : "are safe central bets."}
          </>
        )}
      </p>
      <p className="flex items-start gap-1.5 text-black/60">
        <TrainFront size={13} className="shrink-0 mt-1" />
        <span>Airport: Metro M2 from Kongens Nytorv or Nørreport runs every few minutes, ~15 min to Terminal 3 — no need to leave more than an hour before you want to be there.</span>
      </p>
    </Shell>
  );
};
